// Project Manager → one project (/pm/projects/:id).
//
// Everything about a single project in one place: who's on it, what was
// discovered, what was generated (and what that cost), every test case, and
// every batch it produced — down to the steps and screenshots of a single case.
//
// The PM's job is to know whether the work is actually happening. That means the
// live generation state has to be visible here, not only inside the tester's
// dashboard where a PM has no reason to look.

import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Globe, Loader2, Users, FileText, Boxes, Bug, AlertTriangle,
  Sparkles, Clock, DollarSign, Layers, Pencil,
} from "lucide-react";
import DataTable from "@/components/dashboard/DataTable";
import CaseDetailModal from "@/components/shared/CaseDetailModal";
import EditProjectModal from "@/components/shared/EditProjectModal";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import {
  getProject, pageProjectTestCases, getProjectAssignees, getWorkflow,
} from "@/api/projects";
import { useSurfaceBase } from "@/hooks/useSurfaceBase";
import { listExeBatches } from "@/api/exe";
import { listExtBatches } from "@/api/extension";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/dates";
import { formatDuration, formatUsd } from "@/lib/format";

const PAGE_SIZE = 25;

const fmtWhen = (v) => {
  if (!v) return "—";
  return formatDateTime(v);
};

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="card px-4 py-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Icon size={13} /> {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export default function PmProjectDetail() {
  // These screens are shared with the company Admin surface, so links
  // must stay inside whichever root the reader came in through.
  const base = useSurfaceBase();
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [roster, setRoster] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [batches, setBatches] = useState([]);

  // What is broken RIGHT NOW — the newest batch per runner, not a sum over all
  // history. Summing counted the same broken case once per run, so re-testing an
  // unchanged project 18 times reported 18x the work: this project showed 3,618
  // "open failures" against 275 test cases, and the number could never fall back
  // to zero however much got fixed. The developer's project page already derives
  // it this way; this one didn't, so the two disagreed about the same project.
  const openFailures = useMemo(() => {
    const newest = {};
    for (const b of batches) {                 // each runner's list is newest-first
      const done = (b.passed || 0) + (b.failed || 0) + (b.errored || 0);
      const current = newest[b.kind];
      // Skip past a freshly-launched batch that hasn't produced results yet,
      // or a running project would report zero and hide everything outstanding.
      if (!current || (!current._done && done > 0)) newest[b.kind] = { ...b, _done: done };
    }
    return Object.values(newest)
      .reduce((n, b) => n + (b.failed || 0) + (b.errored || 0), 0);
  }, [batches]);

  const [batchWarning, setBatchWarning] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cases");

  // Test cases page server-side — a 5,000-case project can't be held in one go.
  const [cases, setCases] = useState({ rows: [], total: 0, meta: null, error: null });
  const [casePage, setCasePage] = useState(1);
  const [caseCriteria, setCaseCriteria] = useState({ query: "", filters: {} });
  const [casesLoading, setCasesLoading] = useState(true);
  const [openCase, setOpenCase] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Bumped when a generation finishes, to re-pull everything it changed.
  const [refreshKey, setRefreshKey] = useState(0);
  // Guards against a slow earlier response landing after a newer one.
  const caseReq = useRef(0);

  const loadShell = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, r] = await Promise.all([getProject(id), getProjectAssignees(id)]);
      setProject(p);
      setRoster(r);
      // Batches come from both runners; a project can be tested either way.
      // Narrowed by project SERVER-side: asking for the newest 100 company-wide
      // and filtering here lost this project's batches whenever other projects
      // were busier.
      const failed = [];
      const [exe, ext] = await Promise.all([
        listExeBatches(100, { projectId: id }).catch(() => { failed.push("Exe"); return []; }),
        listExtBatches(100, { projectId: id }).catch(() => { failed.push("Extension"); return []; }),
      ]);
      const mine = [
        ...(Array.isArray(exe) ? exe : []).map((b) => ({ ...b, kind: "exe" })),
        ...(Array.isArray(ext) ? ext : []).map((b) => ({ ...b, kind: "extension" })),
      ].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      setBatches(mine);
      // A dead runner API used to render as "no batches", indistinguishable from
      // a project that genuinely has none.
      setBatchWarning(
        failed.length
          ? `${failed.join(" and ")} batches could not be loaded, so the counts below are incomplete.`
          : null,
      );
    } catch (e) {
      setError((e && e.message) || "Could not load this project.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCases = useCallback(async () => {
    const seq = ++caseReq.current;
    setCasesLoading(true);
    try {
      const res = await pageProjectTestCases(id, {
        limit: PAGE_SIZE,
        offset: (casePage - 1) * PAGE_SIZE,
        search: caseCriteria.query || undefined,
        category: caseCriteria.filters.category !== "all"
          ? caseCriteria.filters.category : undefined,
      });
      if (seq !== caseReq.current) return;          // a newer request already won
      setCases({
        rows: res.test_cases || [],
        total: res.filtered_total ?? res.total ?? 0,
        meta: res,
        error: null,
      });
    } catch (e) {
      if (seq !== caseReq.current) return;
      // Never render a failed fetch as an empty project — "0 test cases" reads as
      // a fact about the project rather than a fact about the request.
      setCases({ rows: [], total: 0, meta: null, error: (e && e.message) || "Could not load the test cases." });
    } finally {
      if (seq === caseReq.current) setCasesLoading(false);
    }
  }, [id, casePage, caseCriteria]);

  useEffect(() => { loadShell(); }, [loadShell, refreshKey]);
  useEffect(() => { loadCases(); }, [loadCases, refreshKey]);

  // Poll the workflow only while something is actually running — a PM watching a
  // generation needs it live, but an idle project must not poll forever.
  useEffect(() => {
    let timer = null;
    let stopped = false;
    let failures = 0;
    let wasBusy = false;
    async function tick() {
      try {
        const w = await getWorkflow(id);
        if (stopped) return;
        failures = 0;
        setWorkflow(w);
        const busy = !!w && (w.state === "running" || w.state === "paused");
        if (busy) {
          wasBusy = true;
          timer = setTimeout(tick, 4000);
        } else if (wasBusy) {
          // It just finished. The suite, the stat tiles and the "Latest
          // generation" card all changed — without this they stayed stale until
          // the PM reloaded the browser.
          wasBusy = false;
          setRefreshKey((n) => n + 1);
        }
      } catch (e) {
        // A single failed poll used to kill the loop for good, freezing the
        // "in progress" banner forever. Back off and retry a few times instead.
        if (stopped) return;
        failures += 1;
        if (failures <= 5) timer = setTimeout(tick, 10000);
      }
    }
    tick();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [id]);

  const assignedTesters = roster?.assigned_testers || [];
  const assignedDevs = roster?.assigned_developers || [];
  const generation = cases.meta?.generation;

  const caseColumns = [
    {
      key: "instruction", header: "Test case",
      render: (r) => (
        <p className="line-clamp-2 max-w-md text-slate-700 dark:text-slate-200">
          {r.instruction}
        </p>
      ),
    },
    {
      // Not sortable: this table pages SERVER-side and the endpoint has no sort
      // parameter, so the header only moved the arrow and bounced you to page 1
      // without reordering anything.
      key: "category", header: "Category",
      render: (r) => <Badge tone="sky" className="capitalize">{r.category || "—"}</Badge>,
    },
    {
      key: "page_url", header: "Page",
      render: (r) => (
        <span className="line-clamp-1 max-w-xs text-xs text-slate-400" title={r.page_url}>
          {r.page_url}
        </span>
      ),
    },
    { key: "priority", header: "Priority" },
  ];

  const batchColumns = [
    {
      key: "name", header: "Batch",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-800 dark:text-slate-100">
            {r.name || r.id.slice(0, 8)}
          </p>
          <p className="text-xs text-slate-400">{fmtWhen(r.created_at)}</p>
        </div>
      ),
    },
    {
      key: "kind", header: "Runner", sortable: true,
      render: (r) => <Badge tone="slate" className="capitalize">{r.kind}</Badge>,
    },
    {
      key: "status", header: "Status", sortable: true,
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: "results", header: "Results",
      // The runners serve cancelled/inconclusive/orphaned as first-class buckets
      // and deliberately do NOT fold them into failed/errored. Omitting them made
      // the row's numbers fail to add up to the batch total.
      render: (r) => (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone="emerald">{r.passed || 0} passed</Badge>
          <Badge tone="rose">{r.failed || 0} failed</Badge>
          {!!r.errored && <Badge tone="amber">{r.errored} errored</Badge>}
          {!!r.cancelled && <Badge tone="slate">{r.cancelled} cancelled</Badge>}
          {!!r.inconclusive && <Badge tone="slate">{r.inconclusive} inconclusive</Badge>}
          {!!r.orphaned && <Badge tone="slate">{r.orphaned} orphaned</Badge>}
        </div>
      ),
    },
    {
      key: "rerun_count", header: "Re-runs", sortable: true,
      render: (r) => <span className="text-slate-500">{r.rerun_count || 0}</span>,
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20 text-slate-400">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="card p-12 text-center">
        <AlertTriangle size={28} className="mx-auto mb-3 text-amber-500" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {error || "That project isn't available."}
        </p>
        <Button className="mt-6" variant="outline" onClick={() => navigate("/pm")}>
          <ArrowLeft size={15} /> Back to projects
        </Button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => navigate("/pm")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-white"
      >
        <ArrowLeft size={15} /> All projects
      </button>

      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {project.name}
          </h1>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            title="Edit name, app URL and description"
            aria-label="Edit project"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-white"
          >
            <Pencil size={15} />
          </button>
        </div>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
          <Globe size={14} /> {project.base_url}
        </p>
      </div>

      <EditProjectModal
        open={editOpen}
        project={project}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => setProject((p) => ({ ...p, ...saved }))}
      />

      {/* live generation — the whole reason a PM opens this page mid-flight */}
      {workflow && workflow.state === "running" && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-brand-500/10 px-4 py-3 text-sm text-brand-800 dark:text-brand-200">
          <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin" />
          <span>
            <strong className="capitalize">{workflow.kind || "Work"}</strong> in progress
            {workflow.phase ? ` — ${workflow.phase}` : ""}
            {workflow.message ? `: ${workflow.message}` : ""}
          </span>
        </div>
      )}
      {workflow && workflow.state === "failed" && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{workflow.error || workflow.message || "The last run failed."}</span>
        </div>
      )}

      {assignedTesters.length === 0 && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            No tester on this project, so nothing can be generated or run. Assign one
            from the Projects list.
          </span>
        </div>
      )}

      {batchWarning && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{batchWarning}</span>
          <button type="button" onClick={loadShell} className="shrink-0 font-semibold underline">
            Retry
          </button>
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={FileText} label="Test cases" value={cases.error ? "—" : (cases.meta?.total ?? 0)}
              hint={`${cases.meta?.pages_with_tests ?? 0} pages covered`} />
        <Stat icon={Boxes} label="Batches" value={batches.length}
              hint="across both runners" />
        <Stat icon={Bug} label="Open failures"
              value={openFailures}
              hint="in the latest run of each runner" />
        <Stat icon={Users} label="On this project"
              value={assignedTesters.length + assignedDevs.length}
              hint={`${assignedTesters.length} tester · ${assignedDevs.length} dev`} />
      </div>

      {/* generation cost/time — what producing this suite actually took */}
      {generation && (
        <div className="card mb-6 p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            <Sparkles size={15} className="text-brand-500" /> Latest generation
          </h3>
          <div className="grid gap-4 text-sm sm:grid-cols-4">
            <div>
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
                <Layers size={12} /> Version
              </p>
              <p className="mt-0.5 font-semibold text-slate-800 dark:text-slate-100">
                v{generation.version} · {generation.tests_total} cases
              </p>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
                <DollarSign size={12} /> Cost
              </p>
              <p className="mt-0.5 font-semibold text-slate-800 dark:text-slate-100">
                {/* toFixed(3) rendered anything under half a tenth of a cent as
                    "$0.000", which reads as free. formatUsd shows "<$0.001"
                    instead, and an ABSENT cost as "—" rather than a confident 0. */}
                {formatUsd(generation.cost_usd)}
              </p>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
                <Clock size={12} /> Took
              </p>
              <p className="mt-0.5 font-semibold text-slate-800 dark:text-slate-100">
                {/* Was raw seconds — a half-hour generation read as "2211s". */}
                {formatDuration(generation.duration_seconds)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">When</p>
              <p className="mt-0.5 font-semibold text-slate-800 dark:text-slate-100">
                {fmtWhen(generation.created_at)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* who's on it */}
      {(assignedTesters.length > 0 || assignedDevs.length > 0) && (
        <div className="card mb-6 p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            <Users size={15} className="text-brand-500" /> The team
          </h3>
          <div className="flex flex-wrap gap-2">
            {assignedTesters.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-700 dark:text-brand-300">
                {p.name || p.email} · tester
              </span>
            ))}
            {assignedDevs.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300">
                {p.name || p.email} · developer
              </span>
            ))}
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
        {[
          { key: "cases", label: `Test cases (${cases.error ? "—" : (cases.meta?.total ?? 0)})` },
          { key: "batches", label: `Batches (${batches.length})` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
              tab === t.key
                ? "bg-white text-slate-900 shadow-soft dark:bg-white/10 dark:text-white"
                : "text-slate-500 hover:text-slate-800 dark:hover:text-white",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {cases.error && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{cases.error}</span>
          <button type="button" onClick={loadCases} className="shrink-0 font-semibold underline">
            Retry
          </button>
        </div>
      )}

      {tab === "cases" ? (
        <DataTable
          /* Distinct keys: both branches render a DataTable in the same slot, so
             without them React reused one instance and the Batches search term
             leaked back into the server-side Test-cases query on tab switch. */
          key="cases"
          columns={caseColumns}
          rows={cases.rows}
          searchKeys={["instruction", "page_url"]}
          filters={[
            // The generator emits exactly these three; a "ui" chip was offered and
            // could never match a row.
            { key: "category", label: "categories",
              options: ["functional", "navigation", "edge_case"] },
          ]}
          pageSize={PAGE_SIZE}
          maxHeight="32rem"
          onRowClick={setOpenCase}
          emptyLabel={
            casesLoading ? "Loading…"
              : cases.error ? "Couldn't load the test cases."
              : (caseCriteria.query || (caseCriteria.filters.category && caseCriteria.filters.category !== "all"))
                ? "No test cases match those filters."
                : "No test cases generated yet."
          }
          server={{
            total: cases.total,
            page: casePage,
            loading: casesLoading,
            onChange: ({ page, query, filters }) => {
              setCasePage(page);
              setCaseCriteria((prev) =>
                prev.query === query && JSON.stringify(prev.filters) === JSON.stringify(filters)
                  ? prev
                  : { query, filters },
              );
            },
          }}
        />
      ) : (
        <DataTable
          key="batches"
          columns={batchColumns}
          rows={batches}
          searchKeys={["name", "id"]}
          filters={[
            { key: "status", label: "statuses",
              options: [...new Set(batches.map((b) => b.status).filter(Boolean))] },
            { key: "kind", label: "runners", options: ["exe", "extension"] },
          ]}
          pageSize={15}
          maxHeight="32rem"
          onRowClick={(r) => navigate(`${base}/projects/${id}/${r.kind}/${r.id}`)}
          emptyLabel="No batches have been run from this project yet."
        />
      )}

      <CaseDetailModal
        testCase={openCase}
        kind="cloud"
        projectId={id}
        onClose={() => setOpenCase(null)}
      />
    </>
  );
}
